/* eslint-disable import/no-webpack-loader-syntax */

import '@babel/polyfill';

import React from 'react';

import { Auth0Provider } from '@auth0/auth0-react';
import Clarity from '@microsoft/clarity';
import { LicenseInfo } from '@mui/x-license-pro';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { ConnectedRouter } from 'connected-react-router/immutable';
import Gleap from 'gleap';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

import config from 'config/config';
import configureStoreFC from 'configureStore';
import App from 'containers/App';
import LanguageProvider from 'containers/LanguageProvider';
import AuthSessionProvider from 'contexts/AuthSessionProvider';
import FeatureFlagsProvider from 'contexts/FeatureFlagsProvider';
import { translationMessages } from 'i18n';
import 'sanitize.css/sanitize.css';
import 'typeface-roboto'; // eslint-disable-line
import history from 'utils/history';
import localStorageUser from 'utils/localStorageUser';

import '!file-loader?name=[name].[ext]!./assets/images/favicon.ico';
import 'file-loader?name=[name].[ext]!./.htaccess';
import packageJson from '../package.json';

import 'global-styles';

LicenseInfo.setLicenseKey(
  'd3b647da1bc41b1e236c40d3cc52b0e4Tz0xMzM5ODAsRT0xODI0MTYzMTk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMy0yMDI0LEtWPTI=',
);

const initialState = {};
const store = configureStoreFC(initialState, history);
window.finalcadHistory = history;
window.finalcadReduxStore = store;
window.blobStore = {};
window.chunkBlobStore = {};
window.contextMenuStore = {};

const container = document.getElementById('app');
const root = createRoot(container);

if (config.sentry.environment !== 'localhost') {
  Sentry.init({
    dsn: config.sentry.dsn,
    dist: `${process.env.SENTRY_PROJECT}-dist`,
    release: `${process.env.SENTRY_PROJECT}@${packageJson.version}`,
    integrations: [
      new Integrations.BrowserTracing({
        beforeNavigate: context => ({
          ...context,
          name: window.location.pathname
            .replace(
              /\/[0-9a-fA-F]{8}\b-[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/g,
              '/<uuid>',
            )
            .replace(
              /\/[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/g,
              '/<uuid>',
            ),
        }),
      }),
    ],
    environment: config.sentry.environment,
    tracesSampleRate: 1.0,
  });
}

Sentry.setUser({
  id: localStorageUser.getCurrentUserId(),
  email: localStorageUser.getCurrentUserEmail(),
});

if (process.env.NODE_ENV !== 'development') {
  Gleap.initialize(config.gleap.appToken);
  Clarity.init(config.clarityProjectId);
}

// render the application
const render = messages => {
  root.render(
    <FeatureFlagsProvider>
      <Auth0Provider
        domain={config.auth0.domain}
        clientId={config.auth0.clientId}
        useRefreshTokens
        cacheLocation="localstorage"
        authorizationParams={{
          redirect_uri: `${window.location.origin}/login/callback`,
          audience: config.auth0.audience,
        }}
      >
        <Provider store={store}>
          <AuthSessionProvider>
            <ConnectedRouter history={history}>
              <LanguageProvider messages={messages}>
                <App />
              </LanguageProvider>
            </ConnectedRouter>
          </AuthSessionProvider>
        </Provider>
      </Auth0Provider>
    </FeatureFlagsProvider>,
  );
};

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  Promise.resolve(import('intl'))
    .then(() => Promise.all([import('intl/locale-data/jsonp/en')]))
    .then(() => render(translationMessages))
    .catch(err => {
      throw err;
    });
} else {
  render(translationMessages);
}
